import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UpdateFreightPayerModel } from './models/job-update-models/UpdateFreightPayerModel';
import { UpdateJobMetaDataModel } from './models/job-update-models/UpdateJobMetaDataModel';
import { UpdateJobMetricsModel } from './models/job-update-models/UpdateJobMetricsModel';
import { UpdateJobTimeBased } from './models/job-update-models/UpdateTimeBasedModel';
import { UpsertAttachmentModel } from './models/job-update-models/UpsertAttachmentModel';
import { UpdateJobDeliveryInformation } from './models/job-update-models/UpdateJobDeliveryInformationModel';
import { Job } from './models/Job';
import { Movement } from './models/Movement';
import { Time } from '@angular/common';
import { FormatString } from '../constant/format-string';
import { JobServicePromise } from './models/JobServicePromise';
import { JobServicePromiseEnum } from './enums/JobServicePromiseEnum';
import { CustomDateFormatter } from '../ngb-datepicker/NgbDateCustomParserFormatter';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { MovementStatus } from './models/MovementStatus';
dayjs.extend(duration);

@Injectable({
  providedIn: 'root'
})
export class JobbingService {

  constructor(private http$: HttpClient) { }

  getJobs(from: Date | null, to: Date | null) {
    const options = {

      params: {
        from: from?.toUTCString() || '',
        to: to?.toUTCString() || ''
      }
    }
    return this.http$.get<any>(`/api/jobbing/deliverystatus`, options);
  }

  getJobEditableStatus(jobId: number) {
    const options = {
      params: {
        jobId: jobId.toString()
      }
    }
    return this.http$.get<boolean>(`/api/jobbing/editablestatus`, options);
  }

  getJobDetails(jobId: number) {
    return this.http$.get<Job>(`/api/jobbing/${jobId}/details`);
  }

  getCurrentMovement(movements: Movement[]): Movement {
    let movement;
    let max = movements[0];
    for (movement = 1; movement < movements.length; movement++) {
      if (movements[movement].movementId > max.movementId)
        max = movements[movement];
    }
    return max;
  }

  getCurrentMovementStatus(movements: MovementStatus[]): MovementStatus {
    let movement;
    let max = movements[0];
    for (movement = 1; movement < movements.length; movement++) {
      if (movements[movement].movementStatusId > max.movementStatusId)
        max = movements[movement];
    }
    return max;
  }
    
  updateJobFreightPayer(jobId: number, updateFreightPayerModel: UpdateFreightPayerModel) {
    let formParams = new FormData();
    formParams.append('model', JSON.stringify(updateFreightPayerModel));
    return this.http$.post(`/api/jobbing/${jobId}/update/freightPayer`, formParams);
  }

  updateJobMetrics(jobId: number, updateJobMetricsModel: UpdateJobMetricsModel) {
    const formParams = new FormData();
    formParams.append('model', JSON.stringify(updateJobMetricsModel));
    return this.http$.post(`/api/jobbing/${jobId}/update/jobMetrics`, formParams);
  }

  updateJobTimeBased(jobId: number, updateJobTimeBaseModel: UpdateJobTimeBased) {
    const formParams = new FormData();
    formParams.append('model', JSON.stringify(updateJobTimeBaseModel));
    return this.http$.post(`/api/jobbing/${jobId}/update/jobTimeBased`, formParams);
  }

  updateJobMetaData(jobId: number, updateJobMetaDataModel: UpdateJobMetaDataModel) {
    const formParams = new FormData();
    formParams.append('model', JSON.stringify(updateJobMetaDataModel));
    return this.http$.post(`/api/jobbing/${jobId}/update/jobMetaData`, formParams);
  }

  updateJobDeliveryInformation(jobId: number, updateJobDeliveryInformation: UpdateJobDeliveryInformation) {
    const formParams = new FormData();
    formParams.append('model', JSON.stringify(updateJobDeliveryInformation));
    return this.http$.post(`/api/jobbing/${jobId}/update/jobDeliveryInformation`, formParams);
  }

  upsertJobAttachments(jobId: number, attachmentModel: UpsertAttachmentModel, files: File[]) {
    const formParams = new FormData();

    if (files) {
      for (let file of files) {
        if (!(file as any).attachmentId) {
          formParams.append('files', file, file.name);
        }
      }
    }

    formParams.append('model', JSON.stringify(attachmentModel));
    return this.http$.post(`/api/jobbing/${jobId}/update/attachments`, formParams);
  }

  getServicePromise(servicePromise: JobServicePromise, timeValue: Time | null): string {
    if (servicePromise && servicePromise.servicePromiseId === JobServicePromiseEnum.SpecificTime && timeValue) {
      return this.getFormattedServicePromiseTime(timeValue);
    } else {
      return servicePromise.name;
    }
  }
    
  getFormattedServicePromiseTime(timeValue: Time): string {
    const currentDate = new Date();
    const [hours, minutes, seconds] = timeValue.toString().split(':').map(Number);
    currentDate.setHours(hours, minutes, seconds, 0);
    return new CustomDateFormatter().timeFormat(currentDate, FormatString.time, "");
  }

  getDurationString(mills: number): string {
    if (mills && mills > 0) {
        const dur = dayjs.duration(mills);
        const hours = dur.hours();
        const minutes = dur.minutes();

        if (hours === 1 && minutes === 0) {
            return "1 Hour";
        } else if (hours > 1 && minutes === 0) {
            return `${hours} Hours`;
        } else if (hours === 0 && minutes === 1) {
            return "1 minute";
        } else if (hours === 0 && minutes > 0) {
            return `${minutes} minutes`;
        } else if (hours > 0 && minutes === 1) {
            return `${hours} Hours 1 minute`;
        } else {
            return `${hours} Hours ${minutes} minutes`;
        }
    } else {
        return "Invalid Duration";
    }
}
    
  getServicePromises() {
    return this.http$.get<any>(`/api/jobbing/servicepromise`);
  }

  getJobPriorities() {
    return this.http$.get<any>(`/api/jobbing/jobpriority`);
  }

  saveJobbingJob(job: any, documents: File[]) {
    let formParams = new FormData();

    if (documents) {
      for (let file of documents) {
        formParams.append('files', file, file.name);
      }
    }

    formParams.append('model', JSON.stringify(job));

    return this.http$.post<any>(`/api/jobbing`, formParams);
  }
}
